import React, { useEffect, useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import "./style.css";
import ModuleSelctorCards from "../../../../Components/Cards/ModuleSelectorCards";
import GenericModalForm from "../../../../Components/Modals/GenericModalForm";
import GenericModal from "../../../../Components/Modals/GenericModal";
import FuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../../Assets/Icons/car_repair.svg";
import UploadIcon from "../../../../Assets/Icons/upload.svg";
import { useNavigate } from "react-router-dom";
import {
  getAdminModules,
  createModule,
  deleteModule,
} from "../../../../API/Admin/SystemConfiguration/ModulePricing";
import {
  getSettlementConfig,
  updateSettlementConfig,
} from "../../../../API/Admin/SystemConfiguration/SettlementConfig";
import usePageAccess from "../../../../Utlis/usePageAccess";

const MODULE_ICONS = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const getModuleIcon = (code) => MODULE_ICONS[code?.toLowerCase()] || FuelIcon;

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveIconUrl = (icon) => {
  if (!icon) return null;
  if (/^https?:\/\//i.test(icon) || icon.startsWith("blob:")) return icon;
  return `${BACKEND_ORIGIN}${icon.startsWith("/") ? "" : "/"}${icon}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const ADD_MODULE_INITIAL_VALUES = { currency: "JMD" };

const ModulePricing = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("system-configuration");
  const [activeTab, setActiveTab] = useState("Monthly Pricing");

  const tabs = ["Monthly Pricing", "Annually Pricing"];
  const [selectedModule, setSelectedModule] = useState(null);
  const [modules, setModules] = useState([]);
  const [isAddModuleOpen, setIsAddModuleOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [platformFeePercent, setPlatformFeePercent] = useState(0);
  const [isEditFeeOpen, setIsEditFeeOpen] = useState(false);
  const iconInputRef = useRef(null);
  const navigate = useNavigate();

  const handleIconUploadClick = () => {
    iconInputRef.current?.click();
  };

  const handleIconFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const handleEdit = () => {
    if (!selectedModule?.id) {
      message.error("Please select a module to edit.");
      return;
    }

    navigate("/edit-module-price", {
      state: { from: "Module Pricing & Settlements", moduleId: selectedModule.id },
    });
  };

  const handleDeleteClick = () => {
    if (!selectedModule?.id) {
      message.error("Please select a module to delete.");
      return;
    }

    setIsDeleteModalOpen(true);
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Add Module",
        icon: <PlusOutlined />,
        type: "default",
        onClick: () => setIsAddModuleOpen(true),
      },
      {
        text: "Delete Module",
        icon: <DeleteOutlined />,
        type: "default",
        onClick: handleDeleteClick,
      },
      {
        text: "Edit Price",
        icon: <EditOutlined />,
        onClick: handleEdit,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs, selectedModule]);

  const fetchModules = async () => {
    try {
      const res = await getAdminModules();
      const list = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
        setModules(list);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to load modules."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load modules."),
      );
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchSettlementConfig = async () => {
    try {
      const res = await getSettlementConfig();
      const data = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && data) {
        setPlatformFeePercent(parseFloat(data.platform_fee_percent) || 0);
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to load settlement config.",
        ),
      );
    }
  };

  useEffect(() => {
    fetchSettlementConfig();
  }, []);

  const handleSaveFee = async (values) => {
    try {
      const res = await updateSettlementConfig({
        platform_fee_percent: values.platformFeePercent,
      });

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data;
        setPlatformFeePercent(parseFloat(data?.platform_fee_percent) || 0);
        message.success(
          res?.data?.message || "Settlement config updated successfully",
        );
        setIsEditFeeOpen(false);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update settlement config."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to update settlement config.",
        ),
      );
    }
  };

  const handleAddModule = async (values) => {
    if (!iconFile) {
      message.error("Please upload an icon.");
      return;
    }

    try {
      const code = values.name
        ?.trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("code", code);
      formData.append("description", values.description || "");
      // Annual price is computed by the backend (monthly × 12), not sent.
      formData.append("monthly_price_per_vehicle", values.monthlyPrice);
      formData.append("status", values.comingSoon ? "inactive" : "active");
      formData.append("icon", iconFile);

      const res = await createModule(formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Module created successfully");
        setIsAddModuleOpen(false);
        setIconFile(null);
        setIconPreview(null);
        fetchModules();
      } else {
        message.error(getErrorMessage(res?.data, "Failed to create module."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to create module."),
      );
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteModule(selectedModule.id);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Module deleted successfully");
        setSelectedModule(null);
        fetchModules();
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete module."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete module."),
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const isAnnual = activeTab === "Annually Pricing";

  const planOptions = modules.map((mod) => {
    const rate = isAnnual
      ? parseFloat(mod.annual_price_per_vehicle)
      : parseFloat(mod.monthly_price_per_vehicle);
    const isInactive = mod.status !== "active";

    return {
      id: mod.id,
      title: mod.name || "-",
      description: mod.description || "",
      price: Number.isNaN(rate)
        ? "-"
        : `${mod.currency || "JMD"} ${rate.toFixed(2)}/vehicle`,
      icon: (
        <img
          src={resolveIconUrl(mod.icon) || getModuleIcon(mod.code)}
          alt={mod.name || "module"}
        />
      ),
      disabled: isInactive,
      tag: isInactive ? "Coming Soon" : undefined,
    };
  });

  return (
    <div className="module">
      <div className="pricing-selection">
        <div className="report-navigation">
          <div className="nav-tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`nav-item ${activeTab === tab ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        {activeTab === "Monthly Pricing" && (
          <div>
            <ModuleSelctorCards
              options={planOptions}
              isPricingModule={true}
              allowSelection={true}
              selectedId={selectedModule?.id}
              onChange={setSelectedModule}
              footerLabel={"Monthly Rate"}
            />
          </div>
        )}
        {activeTab === "Annually Pricing" && (
          <div>
            <ModuleSelctorCards
              options={planOptions}
              isPricingModule={true}
              allowSelection={true}
              selectedId={selectedModule?.id}
              onChange={setSelectedModule}
              footerLabel={"Annual Rate"}
            />
          </div>
        )}
      </div>

      <div className="settlement-section">
        <div className="header-row">
          <div className="title">Fuel Vendor Platform Fee</div>
          <div className="btn-group">
            {canEdit && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsEditFeeOpen(true)}
              >
                Edit Fee
              </Button>
            )}
          </div>
        </div>

        <div className="settlement-card">
          <label className="settlement-label">
            Platform Fee Percentage <span className="required-mark">*</span>
          </label>
          <div className="settlement-value-box">
            {platformFeePercent.toFixed(2)}
          </div>
        </div>
      </div>

      <GenericModalForm
        open={isEditFeeOpen}
        title="Edit Platform Fee"
        subtitle="Update the platform fee charged to fuel vendors"
        okText="Save Changes"
        cancelText="Cancel"
        fields={[
          {
            name: "platformFeePercent",
            label: "Platform Fee Percentage",
            placeholder: "0.00",
            type: "inputNumber",
            min: 0,
            max: 100,
            step: 0.01,
            rules: [{ required: true, message: "Platform fee is required" }],
          },
        ]}
        initialValues={{ platformFeePercent }}
        onCancel={() => setIsEditFeeOpen(false)}
        onOk={handleSaveFee}
      />

      <GenericModalForm
        open={isAddModuleOpen}
        initialValues={{ currency: "JMD" }}
        title="Add New Module"
        subtitle="Create a new module"
        okText="Add Module"
        cancelText="Cancel"
        fields={[
          {
            name: "name",
            label: "Module Name",
            placeholder: "Enter module name",
            type: "input",
            rules: [{ required: true, message: "Module name is required" }],
          },
          {
            name: "description",
            label: "Description",
            placeholder: "Enter description",
            type: "input",
          },
          {
            name: "currency",
            label: "Currency",
            placeholder: "Select currency",
            type: "input",
            disabled: true,
            rules: [{ required: true, message: "Currency is required" }],
          },
          {
            name: "monthlyPrice",
            label: "Monthly Price per Vehicle",
            placeholder: "0.00",
            type: "inputNumber",
            min: 0,
            rules: [{ required: true, message: "Monthly price is required" }],
          },
          {
            name: "annualPrice",
            label: "Annual Price per Vehicle (Monthly × 12)",
            placeholder: "0.00",
            type: "inputNumber",
            min: 0,
            disabled: true,
          },
          {
            name: "comingSoon",
            label: "Coming Soon",
            description:
              "When on, the module is created as inactive and shown as Coming Soon (it can't be selected).",
            type: "switch",
            defaultValue: false,
          },
        ]}
        onValuesChange={(changed, _all, form) => {
          if (!("monthlyPrice" in changed)) return;
          const monthly = parseFloat(changed.monthlyPrice);
          form.setFieldsValue({
            annualPrice: Number.isFinite(monthly)
              ? parseFloat((monthly * 12).toFixed(2))
              : undefined,
          });
        }}
        showProfileUpload={true}
        avatar={iconPreview}
        avatarPlaceholder={
          <img src={UploadIcon} alt="upload" style={{ width: 24, height: 24 }} />
        }
        uploadButtonText="Upload Icon"
        onUploadClick={handleIconUploadClick}
        onFileChange={handleIconFileChange}
        fileInputRef={iconInputRef}
        onCancel={() => {
          setIsAddModuleOpen(false);
          setIconFile(null);
          setIconPreview(null);
        }}
        onOk={handleAddModule}
        showProfileUpload={true}
      />

      <GenericModal
        open={isDeleteModalOpen}
        title="Delete Module"
        subtitle={`Are you sure you want to delete ${selectedModule?.title || "this module"}?`}
        highlightText={selectedModule?.title || ""}
        showReason={false}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#DC2626"
        iconBgColor="#FEE2E2"
        confirmBtnColor="#DC2626"
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ModulePricing;
